import * as THREE from 'three'
import * as CANNON from 'cannon-es'
import { createSkierModel } from './entities/player/SkierModel'
import { createSlopeMesh, createSlopePhysicsBody, getElevation } from './world/terrain/SlopeTerrain'
import { createTreeLines } from './world/environment/Trees'
import { createMountainBackdrop } from './world/environment/Mountains'
import { createJumpRamps, type RampData } from './entities/props/JumpRamps'
import { createObstacleTrees, type ObstacleTreeData } from './entities/obstacles/ObstacleTrees'
import { createBoulders, type BoulderData } from './entities/obstacles/Boulders'
import { CrashSystem } from './gameplay/CrashSystem'
import { InputManager } from './core/input/InputManager'
import { GameHUD } from './ui/hud/GameHUD'
import { RUNS, DEFAULT_RUN, type RunConfig } from './config/runs'
import { createFinishLine, getFinishLineZ } from './entities/props/FinishLine'
import { ResultsScreen } from './ui/overlays/ResultsScreen'
import { StartScreen } from './ui/overlays/StartScreen'
import { MusicManager } from './core/audio/MusicManager'
import { SkiTracks } from './renderer/SkiTracks'
import { RoosterTail } from './renderer/RoosterTail'
import { Afterburner } from './renderer/Afterburner'

// --- Pick run from URL hash or default ---
const runId = window.location.hash.slice(1) || DEFAULT_RUN
const RUN: RunConfig = RUNS[runId] || RUNS[DEFAULT_RUN]

// ============================================================
// SCENE SETUP
// ============================================================
const scene = new THREE.Scene()
scene.background = new THREE.Color(0x87ceeb)
scene.fog = new THREE.FogExp2(0xdfe9f3, 0.0004)

const renderer = new THREE.WebGLRenderer({ antialias: true })
renderer.setSize(window.innerWidth, window.innerHeight)
renderer.setPixelRatio(window.devicePixelRatio)
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 1.2
document.body.appendChild(renderer.domElement)

// ============================================================
// CAMERA (third-person follow)
// ============================================================
const camera = new THREE.PerspectiveCamera(
  65,
  window.innerWidth / window.innerHeight,
  0.1,
  2000
)

// ============================================================
// LIGHTING
// ============================================================
const sunLight = new THREE.DirectionalLight(0xfff5e6, 2.0)
sunLight.position.set(40, 80, -30)
sunLight.castShadow = true
sunLight.shadow.mapSize.width = 2048
sunLight.shadow.mapSize.height = 2048
sunLight.shadow.camera.near = 0.5
sunLight.shadow.camera.far = 200
sunLight.shadow.camera.left = -40
sunLight.shadow.camera.right = 40
sunLight.shadow.camera.top = 40
sunLight.shadow.camera.bottom = -40
scene.add(sunLight)

const fillLight = new THREE.DirectionalLight(0x8ec8f0, 0.4)
fillLight.position.set(-30, 40, 20)
scene.add(fillLight)

const ambientLight = new THREE.AmbientLight(0x6688aa, 0.5)
scene.add(ambientLight)

const hemiLight = new THREE.HemisphereLight(0x87ceeb, 0xf0f0f0, 0.3)
scene.add(hemiLight)

// ============================================================
// TERRAIN — loaded from run config
// ============================================================
const slopeMesh = createSlopeMesh(RUN)
scene.add(slopeMesh)

// Trees lining the run
const { group: trees, colliders: sideTreeColliders } = createTreeLines(RUN)
scene.add(trees)

// Mountain backdrop
const mountainBackdrop = createMountainBackdrop()
scene.add(mountainBackdrop)

// Jump ramps (if enabled for this run)
const ramps: RampData[] = createJumpRamps(RUN)
for (const ramp of ramps) {
  scene.add(ramp.mesh)
}

// Obstacle trees ON the run (if enabled)
const obstacleTrees: ObstacleTreeData[] = createObstacleTrees(RUN)
for (const tree of obstacleTrees) {
  scene.add(tree.mesh)
}

// Boulders on the slope
const boulders: BoulderData[] = createBoulders(RUN)
for (const boulder of boulders) {
  scene.add(boulder.mesh)
}

// Finish line
const finishLine = createFinishLine(RUN)
scene.add(finishLine)
const finishZ = getFinishLineZ(RUN)

// ============================================================
// PHYSICS WORLD
// ============================================================
const world = new CANNON.World({
  gravity: new CANNON.Vec3(0, 0, 0),  // no physics gravity — we handle Y manually
})
world.broadphase = new CANNON.SAPBroadphase(world)
world.allowSleep = false

// Snow material — low friction for skiing
const snowMaterial = new CANNON.Material('snow')
const skiMaterial = new CANNON.Material('ski')
const snowSkiContact = new CANNON.ContactMaterial(snowMaterial, skiMaterial, {
  friction: 0.01,
  restitution: 0.05,
})
world.addContactMaterial(snowSkiContact)

// Slope physics body
const slopeBody = createSlopePhysicsBody(RUN, world)
slopeBody.material = snowMaterial

// ============================================================
// SKIER
// ============================================================
const skierModel = createSkierModel()
scene.add(skierModel)

// Crash system
const crashSystem = new CrashSystem(scene)

// Ski tracks
const skiTracks = new SkiTracks(scene)

// Rooster tail snow spray
const roosterTail = new RoosterTail(scene)

// Afterburner flames at 100mph+
const afterburner = new Afterburner(scene)

// Physics body for skier (sphere for simplicity)
const skierBody = new CANNON.Body({
  mass: 80, // kg
  shape: new CANNON.Sphere(0.4),
  material: skiMaterial,
  linearDamping: 0.002,  // almost no auto-slowdown — turn to slow down
  angularDamping: 0.99,
})
// Start position: top of the slope, slightly above ground
const startZ = -RUN.length / 2 + 10
const startY = getElevation(0, startZ) + 2
skierBody.position.set(0, startY, startZ)
world.addBody(skierBody)

// ============================================================
// INPUT & HUD
// ============================================================
const input = new InputManager()
const hud = new GameHUD(RUN)
const music = new MusicManager()

// ============================================================
// GAME CONSTANTS
// ============================================================
// Arcade-style movement — direct velocity control, not force-based
const BASE_DOWNHILL_SPEED = 12  // m/s (~27 mph) — always moving this fast minimum
const MAX_SPEED = 67            // m/s (~150 mph)
const STEER_SPEED = 18          // m/s base lateral movement
const BRAKE_DECEL = 15          // m/s² deceleration when braking
const TURN_SPEED_BLEED = 0.995  // very slight speed loss while turning
// No manual jump — only ramps launch you
const MS_TO_MPH = 2.237

// Camera: dynamically follows skier, adjusts based on speed and slope
const CAM_BASE_HEIGHT = 3     // base meters above skier
const CAM_BASE_DISTANCE = 6   // base meters behind skier (uphill)
const CAM_SMOOTH = 0.1        // lerp factor (higher = snappier)

// ============================================================
// GAME STATE
// ============================================================
let rampCooldown = false  // prevent double-launching off same ramp
let inAir = false         // explicit air state — set by ramp/jump, cleared on landing
let runTime = 0           // seconds elapsed
let topSpeed = 0          // top speed in mph
let runFinished = false
const resultsScreen = new ResultsScreen()
const clock = new THREE.Clock()

// Track skier facing direction
let skierYaw = 0

// Debug: expose for Chrome DevTools visual verification
;(window as any).__skier = skierModel
;(window as any).__camera = camera
;(window as any).__scene = scene
;(window as any).__skierBody = skierBody
;(window as any).__music = music

// M key to mute/unmute music
window.addEventListener('keydown', (e) => {
  if (e.code === 'KeyM') {
    music.muted = !music.muted
  }
  if (e.code === 'KeyN') {
    music.next()
  }
})

// ============================================================
// GAME LOOP
// ============================================================
function update(dt: number) {
  // If run is finished, stop updating
  if (runFinished) return

  // If crashing, only update crash animation and camera
  if (crashSystem.isCrashing) {
    crashSystem.update(dt, skierModel)
    return
  }

  // --- Physics step ---
  world.step(1 / 60, dt, 3)

  // =============================================
  // ARCADE MOVEMENT — direct velocity control
  // =============================================
  const velocity = skierBody.velocity
  const isTucking = input.isActive('tuck')
  const isBraking = input.isActive('brake')
  const isSteering = input.isActive('steerLeft') || input.isActive('steerRight')

  // --- Downhill speed ---
  const currentDownhillSpeed = velocity.z
  if (isBraking) {
    // Braking: decelerate quickly
    velocity.z = Math.max(0, currentDownhillSpeed - BRAKE_DECEL * dt)
  } else if (isTucking) {
    // Tucking: speed climbs proportional to tilt intensity (mobile) or full blast (keyboard)
    const tuckPower = input.getTuckIntensity()
    velocity.z = currentDownhillSpeed + (5 + tuckPower * 15) * dt
  } else {
    // Coasting: gradually settle toward base speed
    if (currentDownhillSpeed > BASE_DOWNHILL_SPEED) {
      velocity.z = currentDownhillSpeed - 1.5 * dt  // slow gentle decel
    } else {
      velocity.z = Math.min(currentDownhillSpeed + 4 * dt, BASE_DOWNHILL_SPEED)
    }
  }

  // --- Steering: steady lateral movement ---
  if (input.isActive('steerLeft')) {
    velocity.x = THREE.MathUtils.lerp(velocity.x, STEER_SPEED, 0.2)
    skierYaw = THREE.MathUtils.lerp(skierYaw, -0.5, 0.2)
  } else if (input.isActive('steerRight')) {
    velocity.x = THREE.MathUtils.lerp(velocity.x, -STEER_SPEED, 0.2)
    skierYaw = THREE.MathUtils.lerp(skierYaw, 0.5, 0.2)
  } else {
    velocity.x = THREE.MathUtils.lerp(velocity.x, 0, 0.12)
    skierYaw = THREE.MathUtils.lerp(skierYaw, 0, 0.15)
  }

  // Turning bleeds speed only when NOT tucking — tuck overrides turn drag
  if (isSteering && !isTucking) {
    velocity.z *= TURN_SPEED_BLEED
  }

  // Clamp max speed
  const speed = Math.sqrt(velocity.x ** 2 + velocity.z ** 2)
  if (speed > MAX_SPEED) {
    const scale = MAX_SPEED / speed
    velocity.x *= scale
    velocity.z *= scale
  }

  // --- Keep skier within run boundaries ---
  const halfWidth = RUN.width * 1.5  // can go way off-piste
  if (skierBody.position.x > halfWidth) {
    skierBody.position.x = halfWidth
    skierBody.velocity.x = 0
  } else if (skierBody.position.x < -halfWidth) {
    skierBody.position.x = -halfWidth
    skierBody.velocity.x = 0
  }

  // --- Vertical position: locked to ground unless launched ---
  const terrainY = getElevation(skierBody.position.x, skierBody.position.z)
  const groundLevel = terrainY + 0.4
  const isAirborne = inAir

  if (inAir) {
    // Apply strong gravity every frame — skier MUST come down
    skierBody.velocity.y -= 25 * dt
    skierBody.position.y += skierBody.velocity.y * dt

    // Hard time limit: force landing after 2 seconds no matter what
    // (handled by gravity — at 25 m/s², even 8 m/s up lands in <0.7s)

    // Land when at or below current ground level
    if (skierBody.position.y <= groundLevel) {
      skierBody.position.y = groundLevel
      skierBody.velocity.y = 0
      inAir = false
    }
  } else {
    // On ground: hard lock to terrain
    skierBody.position.y = groundLevel
    skierBody.velocity.y = 0
  }

  // --- Jump (Space key) — small controlled hop ---
  if (input.isActive('jump') && !inAir && !rampCooldown) {
    skierBody.velocity.y = 2.5  // small hop
    inAir = true
    rampCooldown = true
    setTimeout(() => { rampCooldown = false }, 500)
  }

  skierModel.position.set(
    skierBody.position.x,
    skierBody.position.y - 0.15, // snug on snow
    skierBody.position.z
  )

  // --- Ski tracks ---
  skiTracks.update(skierBody.position.x, terrainY, skierBody.position.z, inAir)

  // --- Rooster tail snow spray ---
  const steerDir = input.isActive('steerLeft') ? -1 : input.isActive('steerRight') ? 1 : 0
  roosterTail.update(dt, skierBody.position.x, terrainY, skierBody.position.z, isSteering, steerDir, speed, inAir)

  // --- Afterburner flames at 100mph+ ---
  afterburner.update(dt, skierBody.position.x, skierBody.position.y, skierBody.position.z, speed, inAir)

  // Rotate skier to face movement direction + steer lean
  if (speed > 0.5) {
    const moveAngle = Math.atan2(velocity.x, velocity.z)
    skierModel.rotation.y = moveAngle
  }
  // Tilt for steering
  skierModel.rotation.z = skierYaw * 0.3

  // Slope tilt: lean forward to match the terrain grade
  const slopeAngle = Math.atan(RUN.grade)
  skierModel.rotation.x = THREE.MathUtils.lerp(skierModel.rotation.x, slopeAngle, 0.1)

  // Tuck animation: slight crouch
  const targetScaleY = isTucking ? 0.78 : 1.0   // subtle compression
  const targetScaleXZ = isTucking ? 1.07 : 1.0   // barely wider
  skierModel.scale.y = THREE.MathUtils.lerp(skierModel.scale.y, targetScaleY, 0.12)
  skierModel.scale.x = THREE.MathUtils.lerp(skierModel.scale.x, targetScaleXZ, 0.12)
  skierModel.scale.z = THREE.MathUtils.lerp(skierModel.scale.z, targetScaleXZ, 0.12)

  // --- Ramp interaction: ride up the slope, launch at the lip ---
  for (const ramp of ramps) {
    const dx = skierBody.position.x - ramp.position.x
    const dz = skierBody.position.z - ramp.position.z
    // Ramp extends from z=0 (base) to z=-depth (lip)

    if (Math.abs(dx) < ramp.width / 2 && dz < 0 && dz > -ramp.depth) {
      // Skier is ON the ramp — ride up the slope surface
      const rampProgress = -dz / ramp.depth  // 0 at base, 1 at lip

      // Get the actual ramp height from config
      const rampConfigs: Record<string, number> = { small: 1.5, medium: 3.0, large: 5.0 }
      const maxRampHeight = rampConfigs[ramp.size] || 3.0

      // Raise skier along the ramp surface (quadratic for smooth curve)
      const rampSurfaceY = ramp.position.y + rampProgress * rampProgress * maxRampHeight
      if (rampSurfaceY > terrainY + 0.4) {
        skierBody.position.y = rampSurfaceY + 0.4
      }

      // Launch RIGHT at the lip (last 5%) — speed determines how high you go
      if (rampProgress > 0.95 && !inAir && !rampCooldown) {
        // Faster = more air. Slow = barely a pop.
        const speedFactor = Math.min(currentDownhillSpeed / 20, 2)  // 0-2x based on speed
        skierBody.velocity.y = ramp.launchVelocity * speedFactor
        inAir = true
        rampCooldown = true
        setTimeout(() => { rampCooldown = false }, 800)
      }
    }
  }

  // --- Mogul speed check: going too fast over moguls = crash ---
  if (RUN.moguls && !crashSystem.isCrashing && !inAir) {
    const mogulSpeedLimit = 20  // m/s (~45 mph) — max safe mogul speed
    if (currentDownhillSpeed > mogulSpeedLimit) {
      // Random chance to crash based on how much over the limit
      const overSpeed = currentDownhillSpeed - mogulSpeedLimit
      const crashChance = overSpeed * 0.008  // higher speed = higher chance per frame
      if (Math.random() < crashChance) {
        const vel = new THREE.Vector3(velocity.x, velocity.y, velocity.z)
        crashSystem.triggerCrash(
          skierModel.position.clone(),
          vel,
          skierModel,
          () => {
            const respawnZ = skierBody.position.z + 5
            const respawnY = getElevation(0, respawnZ) + 2
            skierBody.position.set(0, respawnY, respawnZ)
            skierBody.velocity.set(0, 0, 2)
          }
        )
      }
    }
  }

  // --- Obstacle collision (trees + boulders): crash! ---
  if (!crashSystem.isCrashing) {
    const allObstacles = [
      ...obstacleTrees.map(t => ({ pos: t.position, r: t.radius })),
      ...boulders.map(b => ({ pos: b.position, r: b.radius })),
      ...sideTreeColliders.map(t => ({ pos: t.position, r: t.radius })),
    ]
    for (const obs of allObstacles) {
      const dx = skierBody.position.x - obs.pos.x
      const dz = skierBody.position.z - obs.pos.z
      // Skip obstacles far away (performance)
      if (Math.abs(dx) > 5 || Math.abs(dz) > 5) continue
      const dist = Math.sqrt(dx * dx + dz * dz)
      if (dist < obs.r + 0.4) {
        const vel = new THREE.Vector3(velocity.x, velocity.y, velocity.z)
        crashSystem.triggerCrash(
          skierModel.position.clone(),
          vel,
          skierModel,
          () => {
            const respawnZ = skierBody.position.z + 5
            const respawnY = getElevation(0, respawnZ) + 2
            skierBody.position.set(0, respawnY, respawnZ)
            skierBody.velocity.set(0, 0, 2)
          }
        )
        break
      }
    }
  }

  // --- Update crash system ---
  crashSystem.update(dt, skierModel)

  // --- Mountains: emerge on the horizon as you ski down ---
  // Positioned centered on skier, sunk low so peaks just peek above the snow horizon
  // Steeper runs need mountains sunk deeper (camera looks more downhill, sees more base)
  const mountainSink = 120 + RUN.grade * 200
  mountainBackdrop.position.set(skierBody.position.x, terrainY - mountainSink, skierBody.position.z)

  // --- Move sun shadow to follow skier ---
  sunLight.position.set(
    skierBody.position.x + 40,
    skierBody.position.y + 80,
    skierBody.position.z - 30
  )
  sunLight.target.position.copy(skierModel.position)
  sunLight.target.updateMatrixWorld()

  // --- Drone-style camera: stays at ground level, doesn't fly up with skier ---
  const speedFactor = Math.min(speed / MAX_SPEED, 1)
  const camDistance = CAM_BASE_DISTANCE + speedFactor * 4
  const camHeight = CAM_BASE_HEIGHT + speedFactor * 1.5 + RUN.grade * 1.5

  // Camera Y is always based on GROUND terrain, not skier's airborne position
  const camTerrainY = getElevation(skierModel.position.x, skierModel.position.z - camDistance)
  const idealCamPos = new THREE.Vector3(
    skierModel.position.x + 1.5,
    camTerrainY + camHeight,
    skierModel.position.z - camDistance
  )
  camera.position.lerp(idealCamPos, CAM_SMOOTH)

  // Look at the skier — use ground level Y even when airborne so camera stays stable
  const lookY = inAir ? groundLevel + 1.5 : skierModel.position.y + 1.5
  const lookTarget = new THREE.Vector3(
    skierModel.position.x,
    lookY,
    skierModel.position.z + 3
  )
  camera.lookAt(lookTarget)

  // --- Timer and top speed tracking ---
  runTime += dt
  const currentMph = speed * MS_TO_MPH
  if (currentMph > topSpeed) topSpeed = currentMph

  // --- HUD ---
  hud.updateSpeed(currentMph)

  // --- Finish line detection ---
  if (skierBody.position.z > finishZ && !runFinished) {
    runFinished = true
    // Stop the skier
    skierBody.velocity.set(0, 0, 0)
    // Stop music
    music.pause()
    // Show results
    resultsScreen.show(RUN, runTime, topSpeed)
  }
}

function animate() {
  requestAnimationFrame(animate)
  const dt = Math.min(clock.getDelta(), 0.05) // cap delta
  update(dt)
  renderer.render(scene, camera)
}

// ============================================================
// RESIZE
// ============================================================
window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight
  camera.updateProjectionMatrix()
  renderer.setSize(window.innerWidth, window.innerHeight)
})

// Pause/resume music when tab visibility changes
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    music.pause()
  } else if (!runFinished) {
    music.play()
  }
})

// ============================================================
// START
// ============================================================
// Initialize camera position near skier start
camera.position.set(0, startY + CAM_BASE_HEIGHT + RUN.grade * 2, startZ - CAM_BASE_DISTANCE)
camera.lookAt(0, startY + 1, startZ + 6)

// On mobile: show start screen for accelerometer permission, then start
// On desktop: start immediately (music needs a click to start on most browsers)
if (input.isMobile) {
  const startScreen = new StartScreen()
  startScreen.waitForStart(input).then(() => {
    music.play()
    animate()
  })
} else {
  // Desktop: start game, music starts on first user interaction (browser policy)
  const startMusic = () => {
    music.play()
    window.removeEventListener('keydown', startMusic)
    window.removeEventListener('click', startMusic)
  }
  window.addEventListener('keydown', startMusic)
  window.addEventListener('click', startMusic)
  animate()
}
